import { ds } from "js/core/models/dataService";
import { $, Backbone } from "js/vendor";
import { app } from "js/namespaces";
import { controls } from "js/editor/ui";
import renderReactRoot from "js/react/renderReactRoot";
import UpgradeButton from "js/react/components/UpgradeButton";
import { template } from "js/core/utilities/templates";
import { trackActivity } from "js/core/utilities/utilities";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowUpgradeDialog,
    ShowWarningDialog,
    ShowConvertedToClassicDialog
} from "js/react/components/Dialogs/BaseDialog";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { FeatureType } from "js/core/models/features";
import { SlideLockType } from "common/constants";
import { SlideTemplateInfoPage } from "js/react/views/SlideTemplateInfo/slideTemplateInfoPage";
import { AddSlideController } from "js/react/views/AddSlide/AddSlideController";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { ExportToPPT } from "js/exporter/exportToPPT";
import { UIController } from "js/editor/dialogs/UIController";
import { TeamSlideDialog } from "js/editor/TeamResources/TeamSlideDialog";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import UserOptionsContainer from "js/react/views/UserOptions/UserOptionsContainer";
import { CreateSharedSlideDialog } from "js/react/views/UserOptions/dialogs/CreateSharedSlideDialog";
import React, { Fragment } from "react";
import { ConvertSlideToAuthoring } from "js/editor/elementUI/authoring/Editors/ConvertToAuthoring";
import { renderUserMenu } from "js/editor/components/UserMenu";
import { linkedSlides } from "js/core/utilities/conversions";
import { noop } from "lodash-es";
import { handleEditSharedSlide } from "./editShareSlide";
import { SwitchTemplateDialog } from "js/editor/TemplateSwitcher/SwitchTemplateDialog";
import ExportDialog from "js/react/views/PresentationSettings/dialogs/ExportDialog";

const organizeIcon = `<svg xmlns='http://www.w3.org/2000/svg' id='organize-icon' width='19' height='18' viewBox='0 0 19 18'>
    <g id='organize-squares' fill='#eee' fill-rule='evenodd'>
        <rect id='first-box' width='4' height='4' x='1.616' y='.719'/>
        <rect id='second-box' width='4' height='4' x='7.788' y='.719'/>
        <rect id='third-box' width='4' height='4' x='1.616' y='7.216'/>
        <rect id='fourth-box' width='4' height='4' x='1.616' y='13.719'/>
        <rect width='4' height='4' x='7.788' y='13.719'/>
        <rect width='4' height='4' x='7.788' y='7.216'/>
        <rect width='4' height='4' x='13.959' y='13.719'/>
        <rect width='4' height='4' x='13.959' y='7.216'/>
        <rect width='4' height='4' x='13.959' y='.719'/>
    </g>
</svg>`;

export const EditorMenu = Backbone.View.extend({
    className: "editor menu_bar",
    template: template("menuEditor"),

    events: {
        "click .presentation_menu": "togglePresentationSettings",
        "click #present": "present",
        "click #unskip": "unskipSlide",
        "click #debug": "debug",
        "click #template-help": "showTemplateHelp",
    },

    remove: function() {
        this.unmount && this.unmount();
        Backbone.View.prototype.remove.call(this);
    },

    render: function() {
        let isTemplate = ds.selection.presentation.get("isTemplate");

        if (isTemplate) {
            this.$el.addEl($.div("presentation-type-label", "TEMPLATE"));
        }

        let $presentationTitle = this.$el.addEl($.div("title presentation_name tooltip"));//.attr("title", "Click to edit your presentation name"));
        $presentationTitle.editbox({
            trim: true,
            change: function(value) {
                if (ds.selection.presentation) {
                    const currentPresentationName = ds.selection.presentation.get("name");
                    if (value !== currentPresentationName) {
                        const eventProps = {
                            from_name: currentPresentationName,
                            to_name: value,
                        };
                        trackActivity("Presentation", "Rename", null, null, eventProps, { audit: true });
                    }

                    ds.selection.presentation.update({ name: value });
                    window.document.title = "Beautiful.ai - " + value;
                }
            }
        });

        let $undo = this.$el.addEl($.div("undo-button"));
        $undo.addEl($.icon("undo"));
        $undo.on("click", () => {
            ds.selection.element = null;
            app.undoManager.undo();
        });

        let $redo = this.$el.addEl($.div("undo-button"));
        $redo.addEl($.icon("redo"));
        $redo.on("click", () => {
            ds.selection.element = null;
            app.undoManager.redo();
        });

        let updateUndoRedo = function() {
            $undo.opacity(app.undoManager.stackPosition == -1 ? 0.3 : 1);
            $redo.opacity(app.undoManager.stackPosition == (app.undoManager.undoStack.length - 1) ? 0.3 : 1);
        };
        this.listenTo(app.undoManager, "undo redo undoStackChanged", () => {
            updateUndoRedo();
        });
        updateUndoRedo();

        this.$el.addEl($.div().css("flex-grow", 2));

        this.$lockedSlideIconContainer = this.$el.addEl($.div("locked-slide").append($.icon("lock")));
        this.$lockedSlideIconContainer.hide();
        this.$lockedSlideIconContainer.addEl($.div("message")).hide();

        let $slideInfo = this.$el.addEl($.div("slide_info"));
        $slideInfo.addEl($.icon(organizeIcon).addClass("organize_icon"));
        $slideInfo.addEl($.label("Slide"));
        $slideInfo.on("click", () => {
            if (app.mainView.editorView.slideGrid) {
                app.mainView.editorView.hideSlideGrid();
            } else {
                app.mainView.editorView.showSlideGrid();
            }
        });

        this.$unskipSlideButton = this.$el.addEl(controls.createButton(this, {
            label: "Unskip slide",
            icon: "visibility",
            callback: () => {
                app.currentCanvas.dataModel.update({ isSkipped: false });
                app.mainView.editorView.updateCurrentCanvasUI();
            }
        }).css({
            background: "#FFAA00",
            height: 30,
            borderRadius: 4,
            padding: "0px 14px",
            marginLeft: 40
        }).addClass("unskip-slide-button").hide());

        this.$el.addEl($.div().css("flex-grow", 2));

        const $upgradeBtn = $.div();

        const workspaceId = ds.selection.presentation.getWorkspaceId();
        if (app.user.features.isFeatureEnabled(FeatureType.UPGRADE, workspaceId)) {
            this.unmount = renderReactRoot(UpgradeButton, { workspaceId }, $upgradeBtn[0]);
            this.$el.append($upgradeBtn);
        }

        let $playButton = this.$el.addEl($.div("menu_button").attr("id", "present").append($.icon("play_circle_filled")));

        $playButton.after(controls.createPopupButton(this, {
            icon: "",
            className: "menu-present-dropdown",
            items: [{
                value: "present",
                label: "Present From Current Slide"
            }, {
                value: "present_from_start",
                label: `Present From Beginning (${navigator.platform == "MacIntel" ? "Cmd" : "Ctrl"}-Click)`
            }, {
                type: "divider"
            }, {
                value: "preview",
                label: "Preview Animation (Shift-Click)",
                beforeShow: () => {
                    return (app.mainView.editorView.slideGrid == null && app.currentCanvas.getEnabledAnimations().length > 0);
                }
            }],
            callback: action => {
                switch (action) {
                    case "present":
                        this.presentFromCurrentSlide();
                        break;
                    case "present_from_start":
                        this.presentFromBeginning();
                        break;
                    case "preview":
                        this.previewAnimation();
                        break;
                }
            }
        }));

        if (!isTemplate) {
            let $share = this.$el.addEl($.div("share-button"));
            $share.addEl($.icon("share"));
            $share.addEl($.label("SHARE"));
            $share.on("click", () => {
                ShowDialog(ShareDialog, { presentation: ds.selection.presentation });
            });
        }

        this.$el.append($.div().css({ width: 50 }));

        renderUserMenu(this.$el, true);

        return this;
    },

    hideUnskipButton() {
        this.$unskipSlideButton.hide();
    },

    showSlideLock(message, onClick) {
        this.$el.find(".locked-slide").show();
        if (message) {
            this.$el.find(".locked-slide .message").empty().append($.div("", message)).show();
            this.$el.find(".locked-slide").addClass("collaboration-warning");

            if (onClick) {
                this.$el.find(".locked-slide").css({ cursor: "pointer" }).off().on("click", onClick);
            }
        }
    },

    hideSlideLock() {
        this.$el.find(".locked-slide").hide();
        this.$el.find(".locked-slide").removeClass("collaboration-warning");
        this.$el.find(".locked-slide .message").empty().hide();
    },

    showCollaborationLock(user) {
        this.$el.find(".locked-slide").show();
        this.$el.find(".locked-slide").addClass("collaboration-warning");
        this.$el.find(".locked-slide .message").empty().append($.div("", `${user.displayName} is editing this slide`)).show();
    },

    hideCollaborationLock() {
        if (!ds.selection.slide?.isLibrarySlide()) {
            this.$el.find(".locked-slide").hide();
        }
        this.$el.find(".locked-slide").removeClass("collaboration-warning");
        this.$el.find(".locked-slide .message").empty().hide();
    },

    present: function(event) {
        ds.selection.element = null;

        if (event.shiftKey) {
            this.previewAnimation();
        } else if (event.metaKey || event.ctrlKey) {
            this.presentFromBeginning();
        } else {
            this.presentFromCurrentSlide();
        }
    },

    trackPresentationOpen: function(props) {
        trackActivity("Presentation", "PresentOpen", null, null, props, { audit: true });
    },

    presentFromCurrentSlide: function() {
        if (app.mainView.editorView.isPresenting) return;

        let currentSlide = app.mainView.editorView.getCurrentSlideIndex();
        const entry_slide_index = currentSlide > -1 ? currentSlide : 0;
        this.trackPresentationOpen({
            initiated_via: "editor_from_current_slide",
            entry_slide_index: entry_slide_index + 1,
            presentation_slide_count: ds.selection.presentation.getSlideCount(),
        });
        app.mainView.playPresentation(ds.selection.presentation.id, entry_slide_index);
    },

    presentFromBeginning: function() {
        if (app.mainView.editorView.isPresenting) return;

        const entry_slide_index = 0;
        this.trackPresentationOpen({
            initiated_via: "editor_from_beginning",
            entry_slide_index: entry_slide_index + 1,
            presentation_slide_count: ds.selection.presentation.getSlideCount(),
        });
        app.mainView.playPresentation(ds.selection.presentation.id, entry_slide_index);
    },

    previewAnimation: function() {
        app.mainView.editorView.playAnimation();
    },

    togglePresentationSettings: function() {
        if (app.mainView.editorView.isPresentationSettingsOpen) {
            app.mainView.editorView.hidePresentationSettings();
        } else {
            app.mainView.editorView.showPresentationSettings();
        }
    },

    onProfileClicked: function(event) {
        // mobile does not show full user options dialog, only logout
        if (app.isConstrained) {
            const menuWidth = 60;

            var $options = controls._renderMenu({
                items: [{
                    label: "Logout",
                    value: "logout"
                }]
            }, this.onOptionMenuClose.bind(this), this.onUserOption);
            $options.left(this.$avatar.left() - menuWidth / 2);
            $options.top(this.$avatar.bottom());
            $options.css("width", menuWidth);
            $options.find(".menu_item").css("min-width", "unset");
            $("body").append($options);
            $options.clickShield(this.onOptionMenuClose.bind(this, $options), true);
        } else {
            const workspaceId = ds.selection && ds.selection.presentation
                ? ds.selection.presentation.getWorkspaceId()
                : this.app.attributes.presentations.workspaceId;
            ShowDialog(UserOptionsContainer, {
                userMenu: this,
                workspaceId
            });
        }
    },

    showTemplateHelp: function() {
        ShowDialog(SlideTemplateInfoPage, {
            templateId: ds.selection.slide.get("template_id"), isLibrary: false, onCopySlide: slideId => {
                console.log(slideId);
                AddSlideController.replaceSlideInPresentation(slideId, ds.selection.slide);
            }
        });
    },

});

export function getSlideMenu({
    showOnlyConvertToClassicSlide = false
} = {}) {
    const workspaceId = ds.selection.presentation.getWorkspaceId();

    let isLibrarySlide = ds.selection.slide.isLibrarySlide();
    let items = [];

    items.push({
        icon: "change_circle",
        label: "Switch Smart Slide...",
        value: "switch-template",
        showBetaBadge: true,
        enabled: app.currentCanvas.slideTemplate.canSwitchTemplate,
        attrs: { "collab-lock": "" },
    });

    items.push({
        type: "divider"
    });

    items.push({
        icon: "category",
        label: "Convert to Classic Slide",
        value: "convert-to-authoring",
        enabled: app.currentCanvas.slideTemplate.canConvertToAuthoring,
        attrs: { "collab-lock": "" },
        showProBadge: !app.user.features.isFeatureEnabled(FeatureType.CONVERT_TO_CLASSIC, workspaceId),
        upgradeType: UpgradePlanDialogType.UPGRADE_PLAN,
        proBadgeAnalytics: { cta: "convertToAuthoring", ...ds.selection.presentation.getAnalytics() },
        proBadgeWorkspaceId: workspaceId,
    });

    if (!showOnlyConvertToClassicSlide) {
        if (isLibrarySlide && ds.selection.slide.isLibrarySlideInCurrentUserOrg() && app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, workspaceId)) {
            items.push({
                type: "divider"
            }, {
                icon: "info",
                label: "View Team Slide Settings...",
                value: "shared_slide_properties"
            });
        } else {
            if (ds.teams.length && app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, workspaceId) && ds.selection.slide.get("template_id") !== "slidePlaceholder") {
                items.push({
                    type: "divider"
                }, {
                    icon: "present_to_all",
                    label: "Share Slide with Team",
                    value: "add_to_slide_library"
                });
            } else if (!app.user.features.isFeatureEnabled(FeatureType.VIEW_LIBRARY_ITEMS, workspaceId)) {
                items.push({
                    type: "divider"
                }, {
                    icon: "present_to_all",
                    label: "Share Slide with Team",
                    value: "add_to_slide_library_cta",
                    showProBadge: !app.user.features.isFeatureEnabled(FeatureType.VIEW_LIBRARY_ITEMS, workspaceId),
                    upgradeType: UpgradePlanDialogType.UPGRADE_PLAN_TO_TEAM,
                    proBadgeAnalytics: { cta: "exportToPPT", ...ds.selection.presentation.getAnalytics() },
                    proBadgeWorkspaceId: workspaceId,
                });
            }
        }

        items.push({
            type: "divider"
        });

        if (app.currentCanvas.dataModel.get("isSkipped")) {
            items.push({
                icon: "visibility",
                value: "unskip",
                label: "Unskip Slide",
                attrs: { "collab-lock": "" },
                enabled: !isLibrarySlide
            });
        } else {
            items.push({
                icon: "visibility_off",
                value: "skip",
                label: "Skip Slide",
                attrs: { "collab-lock": "" },
                enabled: !isLibrarySlide
            });
        }

        items.push(
            {
                type: "divider"
            },
            {
                label: "Export Slide",
                type: "menu",
                icon: "cloud_download",
                menu: {
                    items: [
                        {
                            label: "Export Slide to JPEG",
                            icon: "panorama",
                            value: "export_to_jpeg",
                            enabled: !app.mainView?.editorView.isSlideEditor
                        },
                        {
                            label: "Export Slide to PPTX",
                            icon: "cloud_download",
                            value: "export_to_pptx",
                            showProBadge: !app.user.features.isFeatureEnabled(FeatureType.DESKTOP_APP, workspaceId),
                            proBadgeAnalytics: { cta: "exportToPPT", ...ds.selection.presentation.getAnalytics() },
                            proBadgeWorkspaceId: workspaceId,
                        },
                        {
                            type: "divider"
                        },
                        {
                            label: "Export Entire Presentation...",
                            value: "export_presentation"
                        }
                    ],
                    callback: action => {
                        switch (action) {
                            case "export_to_jpeg":
                                app.currentCanvas.dataModel.exportSlideToJpeg();
                                break;
                            case "export_to_pptx":
                                if (app.user.features.isFeatureEnabled(FeatureType.DESKTOP_APP, workspaceId)) {
                                    const exporter = new ExportToPPT();
                                    exporter.export({ slideCanvases: [app.currentCanvas] });
                                } else {
                                    ShowUpgradeDialog({
                                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                                        analytics: { cta: "exportToPPT", ...ds.selection.presentation.getAnalytics() },
                                        workspaceId,
                                    });
                                }
                                break;
                            case "export_presentation":
                                ShowDialog(ExportDialog, { presentation: ds.selection.presentation });
                                break;
                        }
                    }
                },

            },
            {
                type: "divider"
            },
            {
                label: "Version History",
                icon: "restore",
                value: "version_history",
                attrs: { "collab-lock": "" },
                showProBadge: !app.user.features.isFeatureEnabled(FeatureType.REVISION_HISTORY, workspaceId),
                proBadgeAnalytics: { cta: "Revision History", ...ds.selection.presentation.getAnalytics() },
                proBadgeWorkspaceId: workspaceId,
            }
        );
    }

    return {
        overwriteTriggerHandler: true,
        items,

        // must refresh when the menu is opened
        onOpen: () => {
            const isLocked = app.mainView.editorView.lockModel.checkIfOnlyLockActive(SlideLockType.COLLABORATION);
            if (isLocked) {
                $("[collab-lock]").addClass("disabled");
            }
        },

        callback: async action => {
            const workspaceId = ds.selection.presentation.getWorkspaceId();
            const isLibrarySlide = ds.selection.slide.has("libraryItemId");
            const isReadOnly = (ds.selection.slide.isLibrarySlide() && app.mainView.editorView.$lockedSlideShield);
            switch (action) {
                case "switch-template":
                    if (isReadOnly) {
                        handleEditSharedSlide();
                    } else {
                        ShowDialog(SwitchTemplateDialog, {
                            canvas: app.currentCanvas
                        });
                    }
                    break;
                case "convert-to-authoring":
                    if (!app.user.features.isFeatureEnabled(FeatureType.CONVERT_TO_CLASSIC, workspaceId)) {
                        ShowUpgradeDialog({
                            type: UpgradePlanDialogType.UPGRADE_PLAN,
                            analytics: { cta: "ConvertToAuthoring", ...ds.selection.presentation.getAnalytics() },
                            workspaceId: ds.selection.presentation.getWorkspaceId()
                        });
                        return;
                    }

                    if (isReadOnly) {
                        handleEditSharedSlide()
                            .then(async canEdit => {
                                if (canEdit) {
                                    await ConvertSlideToAuthoring(app.currentCanvas);
                                    ShowConvertedToClassicDialog();
                                }
                            });
                    } else {
                        await ConvertSlideToAuthoring(app.currentCanvas);
                        ShowConvertedToClassicDialog();
                    }
                    break;
                case "shared_slide_properties":
                    ShowDialog(TeamSlideDialog, {
                        libraryItemId: ds.selection.slide.get("libraryItemId"),
                        openFromLibrary: false
                    });
                    break;
                case "skip":
                    app.currentCanvas.dataModel.update({ isSkipped: true });
                    app.mainView.editorView.updateCurrentCanvasUI();
                    break;
                case "unskip":
                    app.currentCanvas.dataModel.update({ isSkipped: false });
                    app.mainView.editorView.updateCurrentCanvasUI();
                    break;
                case "add_to_slide_library":
                    if (isLibrarySlide) {
                        ShowWarningDialog({
                            title: "Failed to add slide to library",
                            message: "This slide is already part of another library",
                        });
                        return;
                    }

                    // check for linked data which may change the dialogs that
                    // are shown for the user
                    const containsLinkedData = linkedSlides.detect(ds.selection.slide);
                    const onBeforeSaveSlide = containsLinkedData
                        ? slide => {
                            linkedSlides.remove(slide);
                            slide.commit();
                        }
                        : null;

                    // handles displaying the Share Dialog - possibly may be called
                    // immediately or after showing a warning about linked slides
                    const showShareSlideDialog = () => {
                        ShowDialog(CreateSharedSlideDialog, {
                            slide: ds.selection.slide,
                            presentation: ds.selection.presentation,
                            onBeforeSaveSlide
                        });
                    };

                    // since there's linked data, show a dialog warning that
                    // the linked slides will be unlinked
                    if (containsLinkedData) {
                        return ShowConfirmationDialog({
                            title: "This slide is linked to other parts of your deck.",
                            message: "Sharing it with your team will remove those links. Would you like to continue?",
                            acceptCallback: showShareSlideDialog,
                            cancelCallback: noop
                        });
                    }

                    // there's nothing to warn about so immediately
                    // display the share dialog
                    showShareSlideDialog();
                    break;
                case "add_to_slide_library_cta":
                    const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, workspaceId);
                    const currentPlan = userIsBasicInWorkspace ? "basic" : app.user.analyticsPersonalPlan;
                    window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
                    break;
                case "version_history":
                    if (isLibrarySlide && !app.mainView.editorView.isSlideEditor) {
                        handleEditSharedSlide();
                    } else {
                        UIController.showVersionHistory();
                    }
                    break;
                case "delete_slide":
                    app.mainView.editorView.deleteSlide(ds.selection.slide);
                    break;
            }
        }
    };
}
